import { useState } from 'react';
import { GeralEstabelecimento } from './Geral';
import { ContatoEstabelecimento } from './Contato';
import { useEstabFormContext } from './Context';
import { EnderecoEstabelecimento } from './Address';
import { WhatsappEstabelecimento } from './Whatsapp';
import { PaginaEstabelecimento } from './Pagina';
import { SectionIcon, SettingsNav, SettingsSection } from './SettingsGroup';
import { urlFile } from '../../../../AxiosConfig';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { AsaasPaymentsIntegration } from './AsaasPayments';
import './EstabelecimentoForm.css';
import { ChevronLeft, Globe, MapPin, MessageCircle, Phone, QrCode, Store } from 'lucide-react';
import type { AppIcon } from '../../../../components/BrandIcons';

type SectionKey = 'geral' | 'endereco' | 'contato' | 'whatsapp' | 'pagina' | 'asaas';

type SectionDef = {
    key: SectionKey;
    title: string;
    description: string;
    icon: AppIcon;
    savesForm: boolean;
};

const LogoPreview = ({ logoUrl, name, size }: { logoUrl?: string; name?: string; size: 'sm' | 'lg' }) => (
    <div className={`estab-thumb shrink-0 ${size === 'lg' ? 'h-16 w-16' : 'h-11 w-11 !rounded-[0.65rem]'}`}>
        {logoUrl ? (
            <img src={logoUrl} alt={`Logo ${name || 'estabelecimento'}`} className="h-full w-full object-contain p-1.5" />
        ) : (
            <Store className="text-base-content/40" aria-hidden="true" />
        )}
    </div>
);

export const EstabelecimentoForm = () => {
    const { imageId, handleSubmit, loading, name, phoneNumber, city, state, isPhoneNumberWhatsapp } = useEstabFormContext();
    const { user } = useAuthContext();
    const logoUrl = imageId ? `${urlFile}/${imageId}` : undefined;
    const displayName = name || 'Estabelecimento';
    const locationLabel = [city, state].filter(Boolean).join(' / ') || 'Localização não informada';

    const sections: SectionDef[] = [
        {
            key: 'geral',
            title: 'Geral',
            description: 'Nome e logo',
            icon: Store,
            savesForm: true,
        },
        {
            key: 'endereco',
            title: 'Endereço',
            description: 'Localização e mapa',
            icon: MapPin,
            savesForm: true,
        },
        {
            key: 'contato',
            title: 'Contato',
            description: 'Telefone e redes sociais',
            icon: Phone,
            savesForm: true,
        },
        {
            key: 'whatsapp',
            title: 'WhatsApp',
            description: isPhoneNumberWhatsapp ? 'Mensagem de confirmação' : 'Número não usado como WhatsApp',
            icon: MessageCircle,
            savesForm: true,
        },
        {
            key: 'pagina',
            title: 'Página',
            description: 'Link público e publicação',
            icon: Globe,
            savesForm: false,
        },
        ...(user?.root
            ? [
                  {
                      key: 'asaas' as const,
                      title: 'Pagamentos Asaas',
                      description: 'Credenciais e webhook',
                      icon: QrCode,
                      savesForm: false,
                  },
              ]
            : []),
    ];

    const [activeKey, setActiveKey] = useState<SectionKey>('geral');
    // No mobile a navegacao e em dois niveis: lista de secoes e detalhe da secao.
    const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
    const active = sections.find((section) => section.key === activeKey) || sections[0];

    const openSection = (key: SectionKey) => {
        setActiveKey(key);
        setMobileDetailOpen(true);
        window.scrollTo({ top: 0 });
    };

    const sectionPanel = (key: SectionKey) => `${activeKey === key ? 'block' : 'hidden'} space-y-6`;

    return (
        <div className="estab-settings w-full lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] lg:items-start lg:gap-8 xl:grid-cols-[18.5rem_minmax(0,1fr)] xl:gap-10">
            <aside className={`${mobileDetailOpen ? 'hidden lg:block' : 'block'} space-y-6 lg:sticky lg:top-20`}>
                <div className="estab-group flex items-center gap-4 p-4 lg:gap-3 lg:p-3">
                    <div className="lg:hidden">
                        <LogoPreview logoUrl={logoUrl} name={displayName} size="lg" />
                    </div>
                    <div className="hidden lg:block">
                        <LogoPreview logoUrl={logoUrl} name={displayName} size="sm" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="break-words text-[1.0625rem] font-semibold leading-snug text-base-content lg:text-sm">
                            {displayName}
                        </div>
                        <div className="break-words text-sm text-base-content/60 lg:text-xs">{locationLabel}</div>
                        {!!phoneNumber && (
                            <div className="text-xs text-base-content/50">{phoneNumber}</div>
                        )}
                    </div>
                </div>

                <SettingsNav
                    label="Seções do estabelecimento"
                    items={sections}
                    activeKey={activeKey}
                    onOpen={openSection}
                    onSelect={setActiveKey}
                />
            </aside>

            <form
                onSubmit={handleSubmit}
                className={`${mobileDetailOpen ? 'block' : 'hidden lg:block'} min-w-0`}
            >
                <div className="mb-5 space-y-2 lg:mb-6">
                    <button
                        type="button"
                        className="sf-back-button -ml-1 lg:hidden"
                        onClick={() => setMobileDetailOpen(false)}
                    >
                        <ChevronLeft aria-hidden="true" />
                        Ajustes
                    </button>
                    <div className="flex items-center gap-3">
                        <SectionIcon icon={active.icon} className="text-base-content/45" />
                        <h2 className="text-[1.375rem] font-bold tracking-tight text-base-content lg:text-xl">
                            {active.title}
                        </h2>
                    </div>
                </div>

                <div className={sectionPanel('geral')}>
                    <GeralEstabelecimento />
                </div>

                <div className={sectionPanel('endereco')}>
                    <EnderecoEstabelecimento />
                </div>

                <div className={sectionPanel('contato')}>
                    <ContatoEstabelecimento />
                </div>

                <div className={sectionPanel('whatsapp')}>
                    <WhatsappEstabelecimento />
                </div>

                <div className={sectionPanel('pagina')}>
                    <PaginaEstabelecimento />
                </div>

                {user?.root && (
                    <div className={sectionPanel('asaas')}>
                        <SettingsSection
                            title="Integração"
                            footer="Credenciais técnicas usadas para cobrar os pagamentos de agendamento."
                            padded
                        >
                            <AsaasPaymentsIntegration />
                        </SettingsSection>
                    </div>
                )}

                {active.savesForm && (
                    <div className="estab-savebar mt-6">
                        <button
                            className="btn btn-primary w-full sm:w-auto sm:min-w-[11rem]"
                            type="submit"
                            disabled={loading}
                        >
                            {loading && <span className="loading loading-spinner loading-sm"></span>}
                            Salvar alterações
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};
